import React, { useState } from "react";
import {
  Phone,
  MessageCircle,
  CheckCircle2,
  Star,
  Menu,
  X,
  Wrench,
  Droplets,
  Cog,
  Cpu,
  Boxes,
  Hammer,
  Sparkles,
  ShieldCheck,
  Users,
  BadgeCheck,
  ChevronRight,
  ChevronLeft,
  ChevronRight as Chevron,
  Settings,
  MapPin,
  Clock,
} from "lucide-react";
import {
  BRAND,
  PHONE_DISPLAY,
  TEL_LINK,
  WHATSAPP_LINK,
  trackLeadConversion,
} from "@/constants/contact";

/* ---------- DATA ---------- */
const heroImage = "/hero.png";

const brandsAll = [
  { name: "LG", logo: "https://cdn.simpleicons.org/lg/a50034" },
  { name: "Samsung", logo: "https://cdn.simpleicons.org/samsung/1428a0" },
  { name: "IFB", logo: null, color: "#003a70" },
  { name: "Bosch", logo: "https://cdn.simpleicons.org/bosch/ed1c24" },
  { name: "Whirlpool", logo: null, color: "#0a4595" },
  { name: "Haier", logo: null, color: "#0b6cb7" },
  { name: "Godrej", logo: null, color: "#0a8a3a" },
  { name: "Panasonic", logo: "https://cdn.simpleicons.org/panasonic/0f4ea8" },
  { name: "Siemens", logo: "https://cdn.simpleicons.org/siemens/009999" },
  { name: "Onida", logo: null, color: "#111111" },
  { name: "Videocon", logo: null, color: "#c81e1e" },
  { name: "Lloyd", logo: null, color: "#0f3e8a" },
  { name: "Kelvinator", logo: null, color: "#0b6cb7" },
  { name: "Toshiba", logo: null, color: "#e30613" },
];

const services = [
  { icon: Wrench, title: "General Repair" },
  { icon: Boxes, title: "Drum Repair" },
  { icon: Droplets, title: "Water Leakage" },
  { icon: Cog, title: "Motor Repair" },
  { icon: Cpu, title: "PCB Repair" },
  { icon: Hammer, title: "Installation" },
  { icon: Sparkles, title: "Uninstallation" },
  { icon: Settings, title: "Maintenance" },
];

const areas = [
  "HSR Layout",
  "Electronic City",
  "Whitefield",
  "Koramangala",
  "Marathahalli",
  "Indiranagar",
  "BTM Layout",
  "Bellandur",
  "JP Nagar",
];

const allAreas = [
  ...areas,
  "Jayanagar",
  "Hebbal",
  "Banashankari",
  "Sarjapur Road",
  "Rajajinagar",
  "Yelahanka",
  "Bommanahalli",
  "Mahadevapura",
  "RR Nagar",
  "Hennur",
  "CV Raman Nagar",
  "Kalyan Nagar",
];

/* ---------- COMPONENTS ---------- */
const BrandCard = ({ b }) => (
  <div
    className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm sm:h-20 sm:w-36 sm:px-4"
    data-testid={`brand-${b.name.toLowerCase()}`}
  >
    {b.logo ? (
      <img
        src={b.logo}
        alt={`${b.name} washing machine logo`}
        loading="lazy"
        className="max-h-8 w-auto object-contain sm:max-h-12"
        onError={(e) => {
          e.currentTarget.style.display = "none";
          const fb = e.currentTarget.nextElementSibling;
          if (fb) fb.style.display = "inline-block";
        }}
      />
    ) : null}
    <span
      className="font-display text-xs font-extrabold tracking-tight sm:text-base"
      style={{
        color: b.color || "#0b2a59",
        display: b.logo ? "none" : "inline-block",
      }}
    >
      {b.name}
    </span>
  </div>
);

const ServiceIcon = ({ Icon, label }) => (
  <div
    className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-3 transition hover:-translate-y-0.5 hover:border-[#0b2a59] hover:shadow-md sm:p-4"
    data-testid={`service-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="grid h-12 w-12 place-items-center text-[#0b2a59] sm:h-14 sm:w-14">
      <Icon strokeWidth={1.5} className="h-7 w-7 sm:h-8 sm:w-8" />
    </div>
    <div className="mt-2 text-center text-[11px] font-semibold text-slate-700 sm:text-sm">
      {label}
    </div>
  </div>
);

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const navLinks = [
    { label: "Home", href: "#top" },
    { label: "Services", href: "#services" },
    { label: "Reviews", href: "#reviews" },
    { label: "Areas", href: "#areas" },
  ];

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="mas-landing-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
        <div className="relative mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Centered brand */}
          <a
            href="#top"
            className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5"
            data-testid="brand-logo"
          >
            <img
              src="/logo.jpg"
              alt="Sarkar Services logo"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-[#0b2a59]/10 sm:h-12 sm:w-12"
            />
            <div className="leading-tight">
              <div className="font-display text-base font-extrabold tracking-tight sm:text-xl">
                <span className="text-[#0b2a59]">SARKAR</span>{" "}
                <span className="text-[#e02424]">SERVICES</span>
              </div>
              <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-600 sm:text-[10px]">
                Washing Machine Repair
              </div>
            </div>
          </a>

          <a
            href={TEL_LINK}
            onClick={trackLeadConversion}
            className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-semibold text-[#0b2a59] shadow-sm transition hover:border-[#0b2a59] sm:px-3 sm:py-2 sm:text-xs"
            data-testid="header-call-now"
          >
            <Phone className="mb-0.5 h-4 w-4 text-[#0b2a59] sm:h-5 sm:w-5" />
            <span>Call Now</span>
          </a>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white">
            <div className="mx-auto flex max-w-5xl flex-col px-4 py-1">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="border-b border-slate-100 py-3 text-sm font-semibold text-slate-700 last:border-b-0"
                  data-testid={`menu-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section
        id="top"
        className="relative overflow-hidden bg-gradient-to-b from-slate-100 to-slate-50"
      >
        <div className="mx-auto max-w-5xl px-4 pb-5 pt-6 sm:pt-8">
          <div className="grid grid-cols-12 gap-2 sm:gap-6">
            {/* Left: copy */}
            <div className="col-span-7">
              <h1
                className="font-display text-[20px] font-extrabold uppercase leading-[1.05] tracking-tight text-[#0b2a59] sm:text-4xl md:text-5xl"
                data-testid="hero-heading"
              >
                Washing Machine
                <br />
                <span className="text-[#0b2a59]">Repair in Bangalore</span>
              </h1>
              <p className="mt-2 text-xs font-medium leading-tight text-slate-700 sm:mt-3 sm:text-lg">
                Technician at Your Doorstep
                <br />
                in 45 Minutes
              </p>
              <ul className="mt-3 space-y-1.5 sm:mt-5 sm:space-y-2.5">
                {[
                  "Same Day Service",
                  "90-Day Warranty",
                  "Genuine Spare Parts",
                  "Expert & Verified Technicians",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-center gap-1.5 text-[11px] font-medium leading-tight text-slate-800 sm:gap-2.5 sm:text-base"
                  >
                    <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[#0b2a59] sm:h-6 sm:w-6">
                      <CheckCircle2
                        className="h-2.5 w-2.5 fill-[#0b2a59] text-white sm:h-3.5 sm:w-3.5"
                        strokeWidth={3}
                      />
                    </span>
                    {b}
                  </li>
                ))}
                <li className="flex items-center gap-1.5 text-[11px] font-medium leading-tight text-slate-800 sm:gap-2.5 sm:text-base">
                  <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[#0b2a59] sm:h-6 sm:w-6">
                    <CheckCircle2
                      className="h-2.5 w-2.5 fill-[#0b2a59] text-white sm:h-3.5 sm:w-3.5"
                      strokeWidth={3}
                    />
                  </span>
                  4.9{" "}
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 sm:h-4 sm:w-4" />{" "}
                  Google Rating
                </li>
              </ul>
            </div>

            {/* Right: Hero image - single technician repairing washing machine */}
            <div
              className="col-span-5 self-end"
              data-testid="hero-image"
            >
              <div className="relative h-[210px] w-full overflow-hidden rounded-xl sm:h-[340px]">
                <img
                  src={heroImage}
                  alt="Verified Sarkar Services technician repairing an appliance in Bangalore"
                  loading="eager"
                  className="absolute inset-0 h-full w-full object-contain object-bottom"
                />
              </div>
            </div>
          </div>

          {/* CTAs - small, side-by-side */}
          <div
            className="mt-4 grid grid-cols-2 gap-2 sm:mt-5 sm:gap-3"
            data-testid="hero-ctas"
          >
            <a
              href={TEL_LINK}
              onClick={trackLeadConversion}
              className="flex items-center justify-center gap-2 rounded-lg bg-[#0b2a59] px-3 py-2.5 text-white shadow-sm transition hover:brightness-110 sm:py-3"
              data-testid="hero-call-button"
            >
              <Phone className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
              <div className="text-left leading-tight">
                <div className="text-xs font-extrabold uppercase tracking-wide sm:text-sm">
                  Call Now
                </div>
                <div className="text-[9px] font-semibold tracking-wide opacity-90 sm:text-[10px]">
                  {PHONE_DISPLAY}
                </div>
              </div>
            </a>
            <a
              href={WHATSAPP_LINK}
              onClick={trackLeadConversion}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-3 py-2.5 text-white shadow-sm transition hover:brightness-110 sm:py-3"
              data-testid="hero-whatsapp-button"
            >
              <MessageCircle className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
              <div className="text-left leading-tight">
                <div className="text-xs font-extrabold uppercase tracking-wide sm:text-sm">
                  WhatsApp Now
                </div>
                <div className="text-[9px] font-semibold tracking-wide opacity-90 sm:text-[10px]">
                  Quick Response
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="px-4 py-5 sm:py-7">
        <div className="mx-auto grid max-w-5xl grid-cols-4 gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:gap-4 sm:p-5">
          {[
            { icon: Users, value: "18+", label: ["Years", "Experience"] },
            {
              icon: BadgeCheck,
              value: "10,000+",
              label: ["Repairs", "Completed"],
            },
            { icon: Star, value: "4.9/5", label: ["Google", "Rating"] },
            {
              icon: ShieldCheck,
              value: "90 Days",
              label: ["Service", "Warranty"],
            },
          ].map((s, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center"
              data-testid={`stat-${i}`}
            >
              <span className="mb-1 text-slate-400">
                <s.icon strokeWidth={1.5} className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
              <div className="font-display text-base font-extrabold leading-tight text-[#0b2a59] sm:text-2xl">
                {s.value}
              </div>
              <div className="mt-0.5 text-[10px] font-medium leading-tight text-slate-600 sm:text-xs">
                {s.label[0]}
                <br />
                {s.label[1]}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUR SERVICES */}
      <section id="services" className="px-4 py-8 sm:py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-lg font-extrabold uppercase tracking-wide text-[#0b2a59] sm:text-2xl">
            Our Services
          </h2>
          <div className="mt-5 grid grid-cols-4 gap-2 sm:gap-4">
            {services.map((s) => (
              <ServiceIcon key={s.title} Icon={s.icon} label={s.title} />
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER REVIEWS */}
      <section
        id="reviews"
        className="bg-slate-50 px-4 py-8 sm:py-12"
        data-testid="reviews-section"
      >
        <div className="mx-auto max-w-5xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Google "G" */}
              <div className="shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  className="h-12 w-12 sm:h-16 sm:w-16"
                  aria-hidden
                >
                  <path
                    fill="#FFC107"
                    d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
                  />
                  <path
                    fill="#FF3D00"
                    d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.4C29.7 34.5 27 35.5 24 35.5c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.6 5.4C40.3 36.9 44 30.9 44 24c0-1.2-.1-2.3-.4-3.5z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold uppercase tracking-wider text-[#0b2a59] sm:text-base">
                  Customer Reviews
                </div>
                <div className="mt-1 flex items-end gap-3">
                  <span className="font-display text-3xl font-extrabold text-[#0b2a59] sm:text-5xl">
                    4.9
                  </span>
                  <div className="mb-1 flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-yellow-400 text-yellow-400 sm:h-6 sm:w-6"
                      />
                    ))}
                  </div>
                </div>
                <div className="mt-1 text-[11px] font-medium text-slate-600 sm:text-sm">
                  Based on 800+ Google Reviews
                </div>
              </div>
              {/* Avatars + 800+ badge */}
              <div className="hidden items-center sm:flex">
                <div className="flex -space-x-3">
                  {[
                    "https://i.pravatar.cc/64?img=12",
                    "https://i.pravatar.cc/64?img=14",
                    "https://i.pravatar.cc/64?img=15",
                    "https://i.pravatar.cc/64?img=33",
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Happy customer ${i + 1}`}
                      className="h-10 w-10 rounded-full border-2 border-white object-cover sm:h-12 sm:w-12"
                    />
                  ))}
                </div>
                <div className="-ml-2 grid h-10 w-10 place-items-center rounded-full border-2 border-white bg-[#0b2a59] text-[10px] font-extrabold text-white sm:h-12 sm:w-12 sm:text-xs">
                  800+
                </div>
              </div>
            </div>

            {/* Mobile avatars row */}
            <div className="mt-4 flex items-center sm:hidden">
              <div className="flex -space-x-2">
                {[
                  "https://i.pravatar.cc/48?img=12",
                  "https://i.pravatar.cc/48?img=14",
                  "https://i.pravatar.cc/48?img=15",
                  "https://i.pravatar.cc/48?img=33",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Happy customer ${i + 1}`}
                    className="h-9 w-9 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <div className="-ml-1 grid h-9 w-9 place-items-center rounded-full border-2 border-white bg-[#0b2a59] text-[10px] font-extrabold text-white">
                800+
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section
        id="areas"
        className="px-4 pb-4 pt-8 sm:pb-6 sm:pt-12"
        data-testid="areas-section"
      >
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-lg font-extrabold uppercase tracking-wide text-[#0b2a59] sm:text-2xl">
            Service Areas in Bangalore
          </h2>
          <div className="mt-5 grid grid-cols-3 gap-y-2 gap-x-3 text-[12px] font-medium text-slate-800 sm:gap-x-6 sm:text-sm">
            {areas.map((a) => (
              <div
                key={a}
                className="flex items-center gap-1.5"
                data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <CheckCircle2
                  className="h-4 w-4 fill-[#16a34a] text-white"
                  strokeWidth={3}
                />
                <span>{a}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setShowAreaModal(true)}
              className="inline-flex items-center gap-1 text-sm font-bold text-[#0b2a59] underline-offset-2 hover:underline"
              data-testid="view-all-areas"
            >
              View All Areas <Chevron className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* MODAL: all areas */}
      {showAreaModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAreaModal(false)}
          data-testid="areas-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-base font-extrabold uppercase tracking-wider text-[#0b2a59]">
                All Service Areas
              </div>
              <button
                type="button"
                onClick={() => setShowAreaModal(false)}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
                data-testid="close-areas-modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 grid max-h-[60vh] grid-cols-2 gap-y-2 overflow-y-auto text-sm">
              {allAreas.map((a) => (
                <div key={a} className="flex items-center gap-1.5">
                  <CheckCircle2
                    className="h-4 w-4 fill-[#16a34a] text-white"
                    strokeWidth={3}
                  />
                  <span>{a}</span>
                </div>
              ))}
            </div>
            <a
              href={TEL_LINK}
              onClick={trackLeadConversion}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0b2a59] py-3 font-extrabold uppercase tracking-wide text-white"
            >
              <Phone className="h-4 w-4" /> Book Service
            </a>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="bg-[#082147] px-4 pb-24 pt-6 text-slate-300 sm:px-6 sm:pb-10 sm:pt-8" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/logo.jpg"
                alt="Sarkar Services logo"
                className="h-14 w-14 rounded-full object-cover ring-2 ring-white/20"
              />
              <div className="font-display text-lg font-extrabold tracking-tight text-white">
                SARKAR <span className="text-[#e02424]">SERVICES</span>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-400">
              Bangalore&apos;s trusted washing machine repair service. 45-minute doorstep, genuine parts, 90-day warranty.
            </p>
          </div>

          <div>
            <div className="font-display text-sm font-extrabold uppercase tracking-wider text-white">
              Contact
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href={TEL_LINK}
                  onClick={trackLeadConversion}
                  className="inline-flex items-center gap-2 transition hover:text-white"
                  data-testid="footer-phone-link"
                >
                  <Phone className="h-4 w-4 text-[#16a34a]" /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={WHATSAPP_LINK}
                  onClick={trackLeadConversion}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 transition hover:text-white"
                  data-testid="footer-whatsapp-link"
                >
                  <MessageCircle className="h-4 w-4 text-[#16a34a]" /> WhatsApp {PHONE_DISPLAY}
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#16a34a]" /> Bangalore, Karnataka, India
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#16a34a]" /> Open Mon - Sun
              </li>
            </ul>
          </div>

          <div>
            <div className="font-display text-sm font-extrabold uppercase tracking-wider text-white">
              Services
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {services.map((s) => (
                <li key={s.title} className="text-slate-400">
                  {s.title}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-7xl border-t border-white/10 pt-6 text-center text-xs text-slate-400 sm:text-sm">
          © {new Date().getFullYear()} {BRAND}. All rights reserved.
        </div>
      </footer>

      {/* STICKY BOTTOM CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-2 shadow-[0_-10px_30px_-10px_rgba(15,23,42,0.25)]"
        data-testid="sticky-cta-bar"
      >
        <a
          href={TEL_LINK}
          onClick={trackLeadConversion}
          className="flex items-center justify-center gap-2.5 bg-[#0b2a59] px-3 py-3.5 text-white"
          data-testid="sticky-call-button"
        >
          <Phone className="h-5 w-5" />
          <div className="text-left leading-tight">
            <div className="text-sm font-extrabold uppercase tracking-wide">
              Call Now
            </div>
            <div className="text-[10px] font-semibold opacity-90">
              {PHONE_DISPLAY}
            </div>
          </div>
        </a>
        <a
          href={WHATSAPP_LINK}
          onClick={trackLeadConversion}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2.5 bg-[#16a34a] px-3 py-3.5 text-white"
          data-testid="sticky-whatsapp-button"
        >
          <MessageCircle className="h-5 w-5" />
          <div className="text-left leading-tight">
            <div className="text-sm font-extrabold uppercase tracking-wide">
              WhatsApp Now
            </div>
            <div className="text-[10px] font-semibold opacity-90">
              Quick Response
            </div>
          </div>
        </a>
      </div>

      {/* Minimal SR-only brand for SEO */}
      <span className="sr-only">{BRAND} — Washing Machine Repair in Bangalore</span>
    </div>
  );
};

export default LandingPage;
